import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';

export type SwitchSize = 'sm' | 'md' | 'lg';

interface SwitchSignature {
  Args: {
    /**
     * Stable identity for automation: emitted as `data-test-hook` on this
     * component's root, alongside its `data-test-*` kind markers.
     *
     * The call site says WHICH instance this is; the component already says
     * what KIND it is. Together they give a key that survives a re-render,
     * which `id` does not — 74% of ids in these apps are Ember-generated
     * (`…-ember42`) and change whenever the component tree is rebuilt. One
     * hook covers the whole subtree: a descendant control is identified as
     * its nearest ancestor hook plus its own role.
     *
     * Consumed by the ux-audit's per-element interaction ledger to tell "a
     * change broke this control" from "the page re-rendered in a different
     * order". See tuitionio/tio-front-end#10057.
     *
     * @example <Switch @testHook="settings__notifications-enabled" @value={{this.on}} />
     */
    testHook?: string;
    /** Whether the switch is on */
    checked?: boolean;
    /** Called when the switch state changes */
    onChange?: (checked: boolean) => void;
    /** Label text displayed next to the switch */
    label?: string;
    /** Accessible name for the switch button when no visible `label` is shown
     * (sets `aria-label`). Use for compact toggles in grids/tables where the
     * context comes from a row/column rather than an adjacent label. */
    ariaLabel?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages */
    errors?: string[];
    /** Disable the switch */
    isDisabled?: boolean;
    /** Size variant @default "md" */
    size?: SwitchSize;
  };
  Element: HTMLDivElement;
}

const SIZE_CLASSES: Record<SwitchSize, { track: string; thumb: string; translate: string }> = {
  sm: { track: 'w-7 h-4', thumb: 'w-3 h-3', translate: 'translate-x-3' },
  md: { track: 'w-9 h-5', thumb: 'w-3.5 h-3.5', translate: 'translate-x-4' },
  lg: { track: 'w-11 h-6', thumb: 'w-4.5 h-4.5', translate: 'translate-x-5' },
};

/**
 * Reimagined Switch — a zero-dependency toggle switch component.
 *
 * Renders as a button with role="switch" for accessibility.
 * Uses violet accent when checked, matching other reimagined components.
 *
 * @example Basic usage
 * ```gts
 * import Switch from 'tio-ui/components/switch';
 *
 * <Switch
 *   @label="Enable notifications"
 *   @checked={{this.notificationsEnabled}}
 *   @onChange={{this.setNotifications}}
 * />
 * ```
 *
 * @example With hint and errors
 * ```gts
 * <Switch
 *   @label="Two-factor authentication"
 *   @hint="Adds an extra layer of security to your account"
 *   @checked={{this.twoFaEnabled}}
 *   @onChange={{this.setTwoFa}}
 *   @errors={{this.twoFaErrors}}
 * />
 * ```
 */
export default class Switch extends Component<SwitchSignature> {
  private _fieldId = `reimagined-switch-${guidFor(this)}`;
  private _labelId = `reimagined-switch-label-${guidFor(this)}`;

  private get size(): SwitchSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  toggle = () => {
    if (this.args.isDisabled) return;
    this.args.onChange?.(!this.args.checked);
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  };

  <template>
    <div
      data-test-switch
      data-test-hook={{@testHook}}
      class="tio-col-extra-tight {{if @isDisabled 'opacity-50'}}"
      ...attributes
    >
      <div class="tio-row">
        <button
          id={{this._fieldId}}
          data-test-switch-toggle
          type="button"
          role="switch"
          aria-checked={{if @checked "true" "false"}}
          aria-labelledby={{if @label this._labelId}}
          aria-label={{@ariaLabel}}
          aria-invalid={{if this.hasErrors "true"}}
          disabled={{@isDisabled}}
          class="tio-switch__toggle relative tio-inline-row shrink-0 rounded-full border-2 transition-colors duration-200 ease-in-out cursor-pointer
            {{this.sizeClasses.track}}
            {{if @checked 'tio-switch--checked' 'tio-bg-track'}}
            {{if @isDisabled 'cursor-not-allowed'}}"
          {{on "click" this.toggle}}
          {{on "keydown" this.handleKeyDown}}
        >
          <span
            aria-hidden="true"
            class="pointer-events-none inline-block rounded-full tio-bg-white shadow-sm transition-transform duration-200 ease-in-out
              {{this.sizeClasses.thumb}}
              {{if @checked this.sizeClasses.translate 'translate-x-0.5'}}"
          ></span>
        </button>

        {{#if @label}}
          <label
            id={{this._labelId}}
            data-test-switch-label
            for={{this._fieldId}}
            class="tio-text-sm-medium tio-fg-secondary cursor-pointer select-none
              {{if @isDisabled 'cursor-not-allowed'}}"
          >
            {{@label}}
          </label>
        {{/if}}
      </div>

      {{#if @hint}}
        <p data-test-switch-hint class="text-xs tio-fg-muted ml-12">{{@hint}}</p>
      {{/if}}

      {{#if this.hasErrors}}
        <div data-test-switch-errors class="ml-12 space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs tio-fg-danger" role="alert">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { SwitchSignature };
